import React, { useEffect, useRef } from 'react';
import ic_accept from "assets/images/ic_accept.svg";
import ic_reject from "assets/images/ic_reject.svg";
import moment from 'moment';
import { isStringTrimNotEmpty, isDateValid } from 'functions';
import { Spinner } from 'components/loaders/Spinner';
import { useCustomSelector } from 'hooks';
import { Toaster } from 'components/notification/Toaster';

export function useTableColumns({ 
    updatingSingleRowPos, updateSingleOutlet,
    currentTabId
}) {
    const {
        activateOutlet_requesting, suspendOutlet_requesting,
        deleteOutlet_requesting, revertDeleteOutlet_requesting,
        addOutletActivationExclusion_requesting, removeOutletActivationExclusion_requesting,
    } = useCustomSelector("outlet");

    const {
        userPermissions
    } = useCustomSelector("userManagementReducers");

    let isUpdateSuspendOutletAllowed = userPermissions?.Internal_OutletStatus_Suspend;
    let isUpdateActivateOutletAllowed = userPermissions?.Internal_OutletStatus_Activate;

    function getColumn_comID() {
        let columnClassName = "columns__com-id";

        return {
            headerCellClassName: columnClassName + " ",
            Header: "Com ID",
            id: "companyId",
            accessor: "ComId",
            bodyCellClassName: columnClassName + " align-items-center ",
            Cell: (props) => {
                let value = props?.cell?.value + "";
                let value_formatted = isStringTrimNotEmpty(value) ? value : "-";

                return (
                    <div className={"noWrap-text-ellipsis d-flex align-items-center"}>{value_formatted}</div>
                );
            },
            width: 100
        }
    }

    function getColumn_outletID() {
        let columnClassName = "table__column-outlet-id";
    
        return {
            headerCellClassName: columnClassName + " ",
            Header: "Outlet ID",
            id: "outletId",
            accessor: "ID",
            bodyCellClassName: columnClassName + " align-items-center ",
            Cell: (props) => {
                let value = props?.cell?.value;
                let value_formatted = isStringTrimNotEmpty(value)? value: "-";
    
                return (
                    <div className={"noWrap-text-ellipsis"}>{value_formatted}</div>
                );
            },
            width: 300
        }
    }

    function getColumn_outlet() {
        let columnClassName = "columns__outlet flex-fill";

        return {
            headerCellClassName: columnClassName + " ",
            Header: "Outlet",
            id: "outlet",
            accessor: "LOCATIONNAME",
            bodyCellClassName: columnClassName + " align-items-center ",
            Cell: (props) => {
                let value = props?.cell?.value;
                let value_formatted = isStringTrimNotEmpty(value)? value: "-";
    
                return (
                    <div className={"noWrap-text-ellipsis"}>{value_formatted}</div>
                );
            },
        }
    }

    function getColumns_suspendDate() {
        let columnClassName = "table__suspend-date";
    
        return {
            headerCellClassName: columnClassName + " ",
            Header: "Suspend Date",
            id: "suspendDate",
            accessor: "SuspendDate",
            bodyCellClassName: columnClassName + " ",
            Cell: (props) => {
                let value = props?.cell?.value;
                let value_formatted = "-";
                if (isDateValid(value))
                    value_formatted = moment(value).format("DD/MM/YYYY");
    
                return (
                    <div className={"noWrap-text-ellipsis"}>{value_formatted}</div>
                );
            },
        }
    }

    function getColumns_paymentDueDate() {
        let columnClassName = "table__payment-due";
    
        return {
            headerCellClassName: columnClassName + " ",
            Header: "Payment Due",
            id: "paymentDue",
            accessor: "PaymentDueDate",
            bodyCellClassName: columnClassName + " ",
            Cell: (props) => {
                let value = props?.cell?.value;
                let value_formatted = "-";
                if (isDateValid(value))
                    value_formatted = moment(value).format("DD/MM/YYYY");
    
                return (
                    <div className={"noWrap-text-ellipsis"}>{value_formatted}</div>
                );
            },
        }
    }

    function getColumn_action() {
        let columnClassName = "columns__action";

        return {
            headerCellClassName: columnClassName + " ",
            Header: "Action",
            id: "action",
            accessor: "action",
            bodyCellClassName: columnClassName + " align-items-center ",
            Cell: (props) => {
                let rowIndex = props?.row?.index;
                let isActionRequired = true;

                return (
                    <div className={"d-flex"}>
                        {/* Activate */}
                        {(currentTabId === "suspended") ? (
                            <div className={"action__accept d-flex align-items-center " +
                                    (!isActionRequired? "invisible ":"") +
                                    ((updatingSingleRowPos.current == rowIndex) && activateOutlet_requesting? "action__accept_visible ":"") +
                                    (isUpdateActivateOutletAllowed? "":"action__accept_disabled ")}
                                onClick={() => {
                                    if (isUpdateActivateOutletAllowed) updateSingleOutlet(props?.row, "activate");
                                    else {
                                        Toaster({
                                            type: "error",
                                            msg: "'Legacy Tool: Update' permission required"
                                        })
                                    }
                                }}
                            >
                                {(updatingSingleRowPos.current == rowIndex) && activateOutlet_requesting? ( 
                                    <Spinner className={"accept__spinner"} size={0} />
                                ):(<img
                                    className={"accept__icon"}
                                    src={ic_accept}
                                />)}
                                <span className={"accept__text"}>Activate</span>
                            </div>
                        ):""}

                        {/* Move to Activation Excluded */}
                        {(currentTabId === "suspended") ? (
                            <div className={"action__reject d-flex align-items-center " +
                                    (!isActionRequired? "invisible ":"") +
                                    ((updatingSingleRowPos.current == rowIndex) && addOutletActivationExclusion_requesting? "action__reject_visible ":"") +
                                    (isUpdateSuspendOutletAllowed? "":"action__reject_disabled ")}
                                onClick={() => {
                                    if (isUpdateSuspendOutletAllowed) updateSingleOutlet(props?.row, "addActivationExclusion");
                                    else {
                                        Toaster({
                                            type: "error",
                                            msg: "'Legacy Tool: Update' permission required"
                                        })
                                    }
                                }}
                            >
                                {(updatingSingleRowPos.current == rowIndex) && addOutletActivationExclusion_requesting? ( 
                                    <Spinner className={"reject__spinner"} size={0} />
                                ):(<img
                                    className={"reject__icon"}
                                    src={ic_reject}
                                />)}
                                <span className={"reject__text"}>Move to Activation Excluded</span>
                            </div>
                        ):""}

                        {/* Move to Suspended */}
                        {(currentTabId === "activationExcluded") ? (
                            <div className={"action__accept d-flex align-items-center " +
                                    (!isActionRequired? "invisible ":"") +
                                    ((updatingSingleRowPos.current == rowIndex) && removeOutletActivationExclusion_requesting? "action__accept_visible ":"") +
                                    (isUpdateSuspendOutletAllowed? "":"action__accept_disabled ")}
                                onClick={() => {
                                    if (isUpdateSuspendOutletAllowed) updateSingleOutlet(props?.row, "removeActivationExclusion");
                                    else {
                                        Toaster({
                                            type: "error",
                                            msg: "'Legacy Tool: Update' permission required"
                                        })
                                    }
                                }}
                            >
                                {(updatingSingleRowPos.current == rowIndex) && removeOutletActivationExclusion_requesting? ( 
                                    <Spinner className={"accept__spinner"} size={0} />
                                ):(<img
                                    className={"accept__icon"}
                                    src={ic_accept}
                                />)}
                                <span className={"accept__text"}>Move to Suspended</span>
                            </div>
                        ):""}

                        {/* Revert */}
                        {(currentTabId === "deleted") ? (
                            <div className={"action__accept d-flex align-items-center " +
                                    (!isActionRequired? "invisible ":"") +
                                    ((updatingSingleRowPos.current == rowIndex) && revertDeleteOutlet_requesting? "action__accept_visible ":"") +
                                    (isUpdateActivateOutletAllowed? "":"action__accept_disabled ")}
                                onClick={() => {
                                    if (isUpdateActivateOutletAllowed) updateSingleOutlet(props?.row, "revert");
                                    else {
                                        Toaster({
                                            type: "error",
                                            msg: "'Legacy Tool: Update' permission required"
                                        })
                                    }
                                }}
                            >
                                {(updatingSingleRowPos.current == rowIndex) && revertDeleteOutlet_requesting? (
                                    <Spinner className={"accept__spinner"} size={0} />
                                ):(<img
                                    className={"accept__icon"}
                                    src={ic_accept}
                                />)}
                                <span className={"accept__text"}>Revert</span>
                            </div>
                        ):""}

                        {/* Suspend */}
                        {(currentTabId === "active")? (
                            <div className={"action__reject d-flex align-items-center " +
                                    (!isActionRequired? "invisible ":"") +
                                    ((updatingSingleRowPos.current == rowIndex) && suspendOutlet_requesting? "action__reject_visible ":"") + 
                                    (isUpdateSuspendOutletAllowed? "":"action__reject_disabled ")}
                                onClick={() => {
                                    if (isUpdateSuspendOutletAllowed) updateSingleOutlet(props?.row, "suspend");
                                    else {
                                        Toaster({
                                            type: "error",
                                            msg: "'Legacy Tool: Update' permission required"
                                        })
                                    }
                                }}
                            >
                                {(updatingSingleRowPos.current == rowIndex) && suspendOutlet_requesting? ( 
                                    <Spinner className={"reject__spinner"} size={0} />
                                ):(<img
                                    className={"reject__icon"}
                                    src={ic_reject}
                                />)}
                                <span className={"reject__text"}>Suspend</span>
                            </div>
                        ):""}

                        {/* Delete */}
                        {(currentTabId === "active")? (
                            <div className={"action__reject d-flex align-items-center " +
                                    (!isActionRequired? "invisible ":"") +
                                    ((updatingSingleRowPos.current == rowIndex) && deleteOutlet_requesting? "action__reject_visible ":"") +
                                    (isUpdateSuspendOutletAllowed? "":"action__reject_disabled ")}
                                onClick={() => {
                                    if (isUpdateSuspendOutletAllowed) updateSingleOutlet(props?.row, "delete");
                                    else {
                                        Toaster({
                                            type: "error",
                                            msg: "'Legacy Tool: Update' permission required"
                                        })
                                    }
                                }}
                            >
                                {(updatingSingleRowPos.current == rowIndex) && deleteOutlet_requesting? (
                                    <Spinner className={"reject__spinner"} size={0} />
                                ):(<img
                                    className={"reject__icon"}
                                    src={ic_reject}
                                />)}
                                <span className={"reject__text"}>Delete</span>
                            </div>
                        ):""}
                    </div>
                );
            },
            width: 250
        }
    }

    return [
        getColumn_comID(),
        getColumn_outletID(),
        getColumn_outlet(),
        getColumns_paymentDueDate(),
        getColumns_suspendDate(),
        getColumn_action(),
    ]
    
}